// Tiny haptics wrapper around navigator.vibrate.
//
// iOS Safari does not implement Vibration API — feature-detect and
// no-op. Android Chrome supports it. Respect prefers-reduced-motion
// (users who opt out of motion almost always also want quiet haptics).
//
// We deliberately keep the patterns short. The Web Vibration API is a
// blunt instrument; long patterns feel weird across devices.

const NOOP = () => {};

function supportsVibrate(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (typeof navigator.vibrate !== 'function') return false;
  if (typeof window !== 'undefined' && window.matchMedia) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  }
  return true;
}

function fire(pattern: number | number[]): void {
  if (!supportsVibrate()) return;
  try {
    navigator.vibrate(pattern);
  } catch {
    // Some browsers throw if vibrate is called from a non-user-gesture
    // context. Ignore — haptics are ornamental.
  }
}

export const haptics = {
  /** Single 10ms blip — neutral confirmation (dose logged, etc.). */
  confirm: supportsVibrate() ? () => fire(10) : NOOP,
  /** Two 10ms blips with a 60ms gap — meaningful success (check-in saved). */
  success: supportsVibrate() ? () => fire([10, 60, 10]) : NOOP,
  /** Single 50ms — heavier acknowledgement (warning accepted). */
  warn: supportsVibrate() ? () => fire(50) : NOOP,
  /** Three quick pulses — celebratory (milestone hit). */
  celebrate: supportsVibrate() ? () => fire([10, 40, 10, 40, 20]) : NOOP,
};

